import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../../components/BodyCopy";
import { Button, Layout, PageTitle } from "lola-framework-ui-test";
import { AuraLayout } from "../../../components/AuraLayout";
import { ErrorAlertIcon as ErrorIcon } from "../../../assets/icons";
import { FlowHeroMark } from "../../../components/FlowHeroMark";
import { useStore } from "../../../store/globalState";
import { useSessionStore } from "../../../store/useSessionStore";
import { getEvents, safeCapture } from "../../../analytics";
import { isHttpUrl, returnToChat } from "../../../utils/returnToChat";
import { ScrollFadeIndicator } from "../../../components/ScrollFadeIndicator";

/**
 * Shown when a single-use Lola link already created a payment.
 * Claim stays 200 so `returnToChatUrl` is still on the session.
 */
export const LinkAlreadyUsedPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const session = useSessionStore((s) => s.session);
  const chatUrl = session.data?.config?.returnToChatUrl;
  const operationId = session.data?.config?.usage?.operationId;
  const canReturnToChat = isHttpUrl(chatUrl);

  const colorConfig = theme.errorView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    iconColors,
    backgroundIcon,
  } = colorConfig;

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("link_already_used"));
  }, []);

  const handleReturnToChat = () => {
    if (!canReturnToChat) return;
    safeCapture(getEvents().EXIT_CLICK, { screen: "link_already_used" });
    returnToChat(chatUrl);
  };

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark backgroundIcon={backgroundIcon}>
            <ErrorIcon size={60} colors={iconColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("error.linkAlreadyUsed")}
              highlightColor={title}
              secudnary=""
              secudnaryColor={title}
            />
            <BodyCopy textColor={bodyCopy}>
              {t("error.linkAlreadyUsedMessage")}
            </BodyCopy>
            {operationId && (
              <BodyCopy textColor={bodyCopy}>
                {t("error.linkAlreadyUsedOperation", { operationId })}
              </BodyCopy>
            )}
          </section>
        </section>
      </Layout.Content>
      {canReturnToChat && (
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="small"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={handleReturnToChat}
            >
              {t("error.returnToChat")}
            </Button>
          </section>
        </Layout.Footer>
      )}
    </AuraLayout>
  );
});
